import { useSuspenseQuery } from "@tanstack/react-query";
import { Controller, useFormContext } from "react-hook-form";
import {
	Combobox,
	ComboboxLabel,
	ComboboxOption,
} from "@/components/catalyst/Combobox";
import {
	Description,
	ErrorMessage,
	Field,
	FieldGroup,
	Fieldset,
	InfoLabel,
	Label,
} from "@/components/catalyst/Fieldset";
import { Heading } from "@/components/catalyst/Heading";
import { Input } from "@/components/catalyst/Input";
import { Select } from "@/components/catalyst/Select";
import { Text } from "@/components/catalyst/Text";
import { orpc } from "@/orpc/client";
import type { CreateReferralLinkFormValues } from "../schema";

/** Step 1: Select partner, enter code, and optionally pick a plan. */
export function StepPartnerAndCode() {
	const {
		register,
		control,
		formState: { errors },
	} = useFormContext<CreateReferralLinkFormValues>();

	const { data: partners } = useSuspenseQuery(
		orpc.partners.list.queryOptions({ input: {} }),
	);

	return (
		<div>
			<Heading level={3}>Partner & Code</Heading>
			<Text className="mt-1 mb-6">
				Choose which partner this referral link is for and set the code users
				will use.
			</Text>

			<Fieldset>
				<FieldGroup>
					<Field>
						<Label>Partner</Label>
						<Controller
							control={control}
							name="partnerId"
							render={({ field: { onChange, value } }) => (
								<Combobox
									options={partners}
									value={partners.find((p) => p.id === value) || null}
									onChange={(partner) => onChange(partner?.id)}
									displayValue={(partner) => partner?.name}
									placeholder="Select a partner"
								>
									{(partner) => (
										<ComboboxOption value={partner}>
											<ComboboxLabel>{partner.name}</ComboboxLabel>
										</ComboboxOption>
									)}
								</Combobox>
							)}
						/>
						{errors.partnerId && (
							<ErrorMessage>{errors.partnerId.message}</ErrorMessage>
						)}
					</Field>

					<Field>
						<Label>Code</Label>
						<Input {...register("slug")} placeholder="e.g. SUMMER-SALE" />
						<Description>
							The code that users will use to claim the offer.
						</Description>
						{errors.slug && <ErrorMessage>{errors.slug.message}</ErrorMessage>}
					</Field>

					<Field>
						<InfoLabel
							info={
								<>
									<p>
										Associate this link with a specific billing interval
										(monthly or yearly).
									</p>
									<p className="mt-1">
										If you plan to attach offer codes, you <strong>must</strong>{" "}
										select Monthly or Yearly; Offer codes require a specific
										plan to match their duration.
									</p>
								</>
							}
						>
							Plan
						</InfoLabel>
						<Controller
							control={control}
							name="planHint"
							render={({ field: { onChange, value } }) => (
								<Select
									value={value ?? ""}
									onChange={(e) =>
										onChange(e.target.value === "" ? null : e.target.value)
									}
								>
									<option value="">Any plan</option>
									<option value="monthly">Monthly</option>
									<option value="yearly">Yearly</option>
								</Select>
							)}
						/>
					</Field>
				</FieldGroup>
			</Fieldset>
		</div>
	);
}
